import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ANNOTATION_COLORS,
  Annotation,
  AnnotationPoint,
  HighlightAnnotation,
  StrokeAnnotation,
  TextAnnotation,
  createAnnotationId,
} from './annotationStorage';

export type AnnotationTool =
  | 'pen'
  | 'highlight'
  | 'text'
  | 'eraser'
  | 'select'
  | null;

interface PdfAnnotationLayerProps {
  width: number;
  height: number;
  annotations: Annotation[];
  tool: AnnotationTool;
  color: string;
  strokeWidth: number; // normalized fraction of width
  fontSize: number; // normalized fraction of width
  onChange: (annotations: Annotation[]) => void;
}

const HIT_THRESHOLD = 0.015; // normalized distance for stroke selection/erase
const HIGHLIGHT_ALPHA = 0.35;
const MIN_HIGHLIGHT_SIZE = 0.01;

interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

let measureCanvas: HTMLCanvasElement | null = null;
const measureText = (
  text: string,
  fontPx: number
): { width: number; height: number } => {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return { width: fontPx, height: fontPx };
  ctx.font = `${fontPx}px sans-serif`;
  const lines = (text || ' ').split('\n');
  let maxWidth = 0;
  lines.forEach((line) => {
    maxWidth = Math.max(maxWidth, ctx.measureText(line || ' ').width);
  });
  return { width: maxWidth, height: lines.length * fontPx * 1.25 };
};

const distToSegment = (
  p: AnnotationPoint,
  a: AnnotationPoint,
  b: AnnotationPoint
): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
};

const drawStroke = (
  ctx: CanvasRenderingContext2D,
  stroke: StrokeAnnotation,
  w: number,
  h: number
) => {
  if (stroke.points.length === 0) return;
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = Math.max(1, stroke.width * w);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  stroke.points.forEach((point, index) => {
    const x = point.x * w;
    const y = point.y * h;
    if (index === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
};

const drawHighlight = (
  ctx: CanvasRenderingContext2D,
  highlight: HighlightAnnotation,
  w: number,
  h: number
) => {
  ctx.save();
  ctx.globalAlpha = HIGHLIGHT_ALPHA;
  ctx.fillStyle = highlight.color;
  ctx.fillRect(highlight.x * w, highlight.y * h, highlight.w * w, highlight.h * h);
  ctx.restore();
};

const PdfAnnotationLayer = ({
  width,
  height,
  annotations,
  tool,
  color,
  strokeWidth,
  fontSize,
  onChange,
}: PdfAnnotationLayerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // transient drawing state
  const drawStrokeRef = useRef<StrokeAnnotation | null>(null);
  const highlightPreviewRef = useRef<HighlightAnnotation | null>(null);
  const highlightStartRef = useRef<AnnotationPoint | null>(null);
  const moveRef = useRef<{ id: string; last: AnnotationPoint } | null>(null);
  const movedRef = useRef(false);
  // drag state for moving a text box directly by grabbing it
  const textDragRef = useRef<{
    id: string;
    startClientX: number;
    startClientY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const getBBox = useCallback(
    (annotation: Annotation): BBox => {
      if (annotation.type === 'highlight') {
        return {
          x: annotation.x,
          y: annotation.y,
          w: annotation.w,
          h: annotation.h,
        };
      }
      if (annotation.type === 'text') {
        const size = measureText(annotation.text, annotation.fontSize * width);
        return {
          x: annotation.x,
          y: annotation.y,
          w: width ? size.width / width : 0,
          h: height ? size.height / height : 0,
        };
      }
      const xs = annotation.points.map((p) => p.x);
      const ys = annotation.points.map((p) => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      return {
        x: minX,
        y: minY,
        w: Math.max(...xs) - minX,
        h: Math.max(...ys) - minY,
      };
    },
    [width, height]
  );

  const hitTest = useCallback(
    (point: AnnotationPoint): Annotation | null => {
      for (let i = annotations.length - 1; i >= 0; i--) {
        const annotation = annotations[i];
        if (annotation.type === 'stroke') {
          for (let j = 1; j < annotation.points.length; j++) {
            if (
              distToSegment(
                point,
                annotation.points[j - 1],
                annotation.points[j]
              ) < HIT_THRESHOLD
            ) {
              return annotation;
            }
          }
          if (
            annotation.points.length === 1 &&
            Math.hypot(
              point.x - annotation.points[0].x,
              point.y - annotation.points[0].y
            ) < HIT_THRESHOLD
          ) {
            return annotation;
          }
        } else {
          const box = getBBox(annotation);
          if (
            point.x >= box.x - 0.005 &&
            point.x <= box.x + box.w + 0.005 &&
            point.y >= box.y - 0.005 &&
            point.y <= box.y + box.h + 0.005
          ) {
            return annotation;
          }
        }
      }
      return null;
    },
    [annotations, getBBox]
  );

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const list: Annotation[] = [...annotations];
    if (drawStrokeRef.current) list.push(drawStrokeRef.current);
    if (highlightPreviewRef.current) list.push(highlightPreviewRef.current);

    // highlights first so strokes/text sit on top
    list
      .filter((a): a is HighlightAnnotation => a.type === 'highlight')
      .forEach((a) => drawHighlight(ctx, a, width, height));
    list
      .filter((a): a is StrokeAnnotation => a.type === 'stroke')
      .forEach((a) => drawStroke(ctx, a, width, height));

    // selection outline
    const selected = annotations.find((a) => a.id === selectedId);
    if (selected) {
      const box = getBBox(selected);
      ctx.save();
      ctx.strokeStyle = '#1976d2';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      const pad = 4;
      ctx.strokeRect(
        box.x * width - pad,
        box.y * height - pad,
        box.w * width + pad * 2,
        box.h * height + pad * 2
      );
      ctx.restore();
    }
  }, [annotations, width, height, selectedId, getBBox]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    redraw();
  }, [width, height, redraw]);

  // clear selection/editing when leaving edit-capable tools
  useEffect(() => {
    if (tool !== 'select') {
      setSelectedId(null);
    }
    if (tool !== 'select' && tool !== 'text') {
      setEditingId(null);
    }
  }, [tool]);

  // drop selection if the annotation disappeared
  useEffect(() => {
    if (selectedId && !annotations.some((a) => a.id === selectedId)) {
      setSelectedId(null);
    }
  }, [annotations, selectedId]);

  const getPoint = (e: React.MouseEvent): AnnotationPoint => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  };

  const commitEditing = useCallback(
    (value: string) => {
      const id = editingId;
      if (!id) return;
      const trimmed = value.replace(/\s+$/g, '');
      setEditingId(null);
      if (!trimmed) {
        onChange(annotations.filter((a) => a.id !== id));
        return;
      }
      onChange(
        annotations.map((a) =>
          a.id === id && a.type === 'text' ? { ...a, text: value } : a
        )
      );
    },
    [annotations, editingId, onChange]
  );

  const startTextEditing = (annotation: TextAnnotation) => {
    setSelectedId(annotation.id);
    setEditingId(annotation.id);
    setEditingValue(annotation.text);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!tool || editingId) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const point = getPoint(e);

    if (tool === 'pen') {
      drawStrokeRef.current = {
        id: createAnnotationId(),
        type: 'stroke',
        color,
        width: strokeWidth,
        points: [point],
      };
      redraw();
      return;
    }

    if (tool === 'highlight') {
      highlightStartRef.current = point;
      highlightPreviewRef.current = {
        id: createAnnotationId(),
        type: 'highlight',
        color,
        x: point.x,
        y: point.y,
        w: 0,
        h: 0,
      };
      redraw();
      return;
    }

    if (tool === 'text') {
      const annotation: TextAnnotation = {
        id: createAnnotationId(),
        type: 'text',
        color,
        x: point.x,
        y: point.y,
        text: '',
        fontSize,
      };
      onChange([...annotations, annotation]);
      startTextEditing(annotation);
      return;
    }

    if (tool === 'eraser') {
      const hit = hitTest(point);
      if (hit) onChange(annotations.filter((a) => a.id !== hit.id));
      return;
    }

    if (tool === 'select') {
      const hit = hitTest(point);
      setSelectedId(hit ? hit.id : null);
      if (hit) {
        moveRef.current = { id: hit.id, last: point };
        movedRef.current = false;
      }
    }
  };

  const translateAnnotation = (
    annotation: Annotation,
    dx: number,
    dy: number
  ): Annotation => {
    if (annotation.type === 'stroke') {
      return {
        ...annotation,
        points: annotation.points.map((p) => ({ x: p.x + dx, y: p.y + dy })),
      };
    }
    return { ...annotation, x: annotation.x + dx, y: annotation.y + dy };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!tool || editingId) return;
    const point = getPoint(e);

    if (tool === 'pen' && drawStrokeRef.current) {
      drawStrokeRef.current.points.push(point);
      redraw();
      return;
    }

    if (tool === 'highlight' && highlightStartRef.current) {
      const start = highlightStartRef.current;
      highlightPreviewRef.current = {
        id: highlightPreviewRef.current?.id || createAnnotationId(),
        type: 'highlight',
        color,
        x: Math.min(start.x, point.x),
        y: Math.min(start.y, point.y),
        w: Math.abs(point.x - start.x),
        h: Math.abs(point.y - start.y),
      };
      redraw();
      return;
    }

    if (tool === 'eraser' && e.buttons === 1) {
      const hit = hitTest(point);
      if (hit) onChange(annotations.filter((a) => a.id !== hit.id));
      return;
    }

    if (tool === 'select' && moveRef.current) {
      const { id, last } = moveRef.current;
      const dx = point.x - last.x;
      const dy = point.y - last.y;
      if (dx === 0 && dy === 0) return;
      movedRef.current = true;
      moveRef.current = { id, last: point };
      onChange(
        annotations.map((a) => (a.id === id ? translateAnnotation(a, dx, dy) : a))
      );
    }
  };

  const handlePointerUp = () => {
    if (tool === 'pen' && drawStrokeRef.current) {
      const stroke = drawStrokeRef.current;
      drawStrokeRef.current = null;
      if (stroke.points.length > 0) onChange([...annotations, stroke]);
      else redraw();
      return;
    }

    if (tool === 'highlight' && highlightPreviewRef.current) {
      const highlight = highlightPreviewRef.current;
      highlightPreviewRef.current = null;
      highlightStartRef.current = null;
      if (highlight.w >= MIN_HIGHLIGHT_SIZE && highlight.h >= MIN_HIGHLIGHT_SIZE) {
        onChange([...annotations, highlight]);
        setSelectedId(highlight.id);
      } else {
        redraw();
      }
      return;
    }

    if (tool === 'select') {
      moveRef.current = null;
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (tool !== 'select') return;
    const point = getPoint(e);
    const hit = hitTest(point);
    if (hit && hit.type === 'text') startTextEditing(hit);
  };

  const handleTextPointerDown = (e: React.PointerEvent, a: TextAnnotation) => {
    if (tool !== 'select' || editingId) return;
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    setSelectedId(a.id);
    movedRef.current = false;
    textDragRef.current = {
      id: a.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: a.x,
      origY: a.y,
    };
  };

  const handleTextPointerMove = (e: React.PointerEvent) => {
    const drag = textDragRef.current;
    const canvas = canvasRef.current;
    if (!drag || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dx = (e.clientX - drag.startClientX) / rect.width;
    const dy = (e.clientY - drag.startClientY) / rect.height;
    if (dx !== 0 || dy !== 0) movedRef.current = true;
    onChange(
      annotations.map((a) =>
        a.id === drag.id && a.type === 'text'
          ? { ...a, x: drag.origX + dx, y: drag.origY + dy }
          : a
      )
    );
  };

  const handleTextPointerUp = () => {
    textDragRef.current = null;
  };

  const applyColorToSelected = (nextColor: string) => {
    if (!selectedId) return;
    onChange(
      annotations.map((a) =>
        a.id === selectedId ? { ...a, color: nextColor } : a
      )
    );
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    onChange(annotations.filter((a) => a.id !== selectedId));
    setSelectedId(null);
  };

  const selected = useMemo(
    () => annotations.find((a) => a.id === selectedId) || null,
    [annotations, selectedId]
  );
  const editing = useMemo(
    () =>
      annotations.find(
        (a) => a.id === editingId && a.type === 'text'
      ) as TextAnnotation | undefined,
    [annotations, editingId]
  );

  const canvasCursor =
    tool === 'text'
      ? 'text'
      : tool === 'select'
      ? 'default'
      : tool
      ? 'crosshair'
      : 'default';

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width,
          height,
          zIndex: 2,
          cursor: canvasCursor,
          pointerEvents: tool ? 'auto' : 'none',
          touchAction: tool ? 'none' : 'auto',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      />

      {/* rendered text annotations (visual only; interactions go through canvas) */}
      {annotations
        .filter((a): a is TextAnnotation => a.type === 'text')
        .filter((a) => a.id !== editingId)
        .map((a) => (
          <div
            key={a.id}
            onPointerDown={(e) => handleTextPointerDown(e, a)}
            onPointerMove={handleTextPointerMove}
            onPointerUp={handleTextPointerUp}
            onPointerCancel={handleTextPointerUp}
            onDoubleClick={(e) => {
              if (tool !== 'select') return;
              e.stopPropagation();
              startTextEditing(a);
            }}
            style={{
              position: 'absolute',
              left: a.x * width,
              top: a.y * height,
              color: a.color,
              fontSize: a.fontSize * width,
              fontFamily: 'sans-serif',
              lineHeight: 1.25,
              whiteSpace: 'pre',
              userSelect: 'none',
              touchAction: tool === 'select' ? 'none' : 'auto',
              cursor: tool === 'select' ? 'move' : 'default',
              pointerEvents: tool === 'select' ? 'auto' : 'none',
              zIndex: 3,
            }}
          >
            {a.text}
          </div>
        ))}

      {/* inline text editor */}
      {editing && (
        <textarea
          autoFocus
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => commitEditing(editingValue)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              commitEditing(editingValue);
            }
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            left: editing.x * width,
            top: editing.y * height,
            color: editing.color,
            fontSize: editing.fontSize * width,
            fontFamily: 'sans-serif',
            lineHeight: 1.25,
            zIndex: 5,
            minWidth: 60,
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid #1976d2',
            outline: 'none',
            resize: 'both',
            padding: 2,
            overflow: 'hidden',
          }}
        />
      )}

      {/* contextual toolbar for the selected annotation */}
      {selected && !editingId && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            left: getBBox(selected).x * width,
            top: Math.max(0, getBBox(selected).y * height - 40),
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 4px',
            borderRadius: 6,
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
            zIndex: 6,
          }}
        >
          {selected.type === 'text' && (
            <IconButton
              size="small"
              onClick={() => startTextEditing(selected as TextAnnotation)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {ANNOTATION_COLORS.map((c) => (
            <div
              key={c}
              onClick={() => applyColorToSelected(c)}
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: c,
                cursor: 'pointer',
                boxShadow: '0 0 0 1px #ccc',
                border:
                  selected.color === c ? '2px solid #333' : '2px solid #fff',
              }}
            />
          ))}
          <IconButton size="small" onClick={deleteSelected}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </>
  );
};

export default PdfAnnotationLayer;
